// 회계 관리 타입 정의

// 계정 유형
export type AccountType =
  | 'asset' // 자산
  | 'liability' // 부채
  | 'equity' // 자본
  | 'income' // 수익
  | 'expense'; // 비용

// 계정 하위 유형
export type AccountSubType =
  | 'bank' // 은행
  | 'cash' // 현금
  | 'receivable' // 매출채권
  | 'payable' // 매입채무
  | 'stock' // 재고자산
  | 'fixed_asset' // 고정자산
  | 'current_asset' // 유동자산
  | 'current_liability' // 유동부채
  | 'long_term_liability' // 장기부채
  | 'capital' // 자본금
  | 'retained_earnings' // 이익잉여금
  | 'sales' // 매출
  | 'cost_of_goods_sold' // 매출원가
  | 'operating_expense' // 영업비용
  | 'other_income' // 영업외수익
  | 'other_expense'; // 영업외비용

// 분개 상태
export type JournalEntryStatus = 'draft' | 'submitted' | 'cancelled';

// 결제 상태
export type PaymentStatus = 'draft' | 'submitted' | 'cancelled';

// 결제 유형
export type PaymentType = 'receive' | 'pay' | 'internal_transfer';

// 결제 방법
export type PaymentMethod = 'cash' | 'bank_transfer' | 'check' | 'card';

// 계정과목
export interface Account {
  id: string;
  code: string;
  name: string;
  accountType: AccountType;
  subType?: AccountSubType;
  parentId?: string;
  isGroup: boolean;
  balance: number;
  currency: string;
  description?: string;
  isActive: boolean;
}

// 분개 항목
export interface JournalEntryItem {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  description?: string;
}

// 분개
export interface JournalEntry {
  id: string;
  code: string;
  entryDate: string;
  status: JournalEntryStatus;
  entryType: string; // 일반분개, 매출분개, 매입분개 등
  items: JournalEntryItem[];
  totalDebit: number;
  totalCredit: number;
  reference?: string;
  remarks?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// 결제
export interface PaymentEntry {
  id: string;
  code: string;
  paymentDate: string;
  paymentType: PaymentType;
  partyType?: 'customer' | 'supplier';
  partyId?: string;
  partyName?: string;
  paymentMethod: PaymentMethod;
  status: PaymentStatus;
  paidFromAccount?: string;
  paidToAccount?: string;
  amount: number;
  reference?: string;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

// 은행 거래
export interface BankTransaction {
  id: string;
  transactionDate: string;
  bankAccount: string;
  bankAccountName: string;
  transactionType: 'deposit' | 'withdrawal';
  amount: number;
  balance: number;
  description: string;
  reference?: string;
  isReconciled: boolean;
  reconciledDate?: string;
}

// 재무제표 항목
export interface FinancialStatementItem {
  accountCode: string;
  accountName: string;
  amount: number;
  previousAmount?: number;
  children?: FinancialStatementItem[];
}

// 대차대조표
export interface BalanceSheet {
  asOf: string;
  assets: {
    currentAssets: FinancialStatementItem[];
    totalCurrentAssets: number;
    fixedAssets: FinancialStatementItem[];
    totalFixedAssets: number;
    totalAssets: number;
  };
  liabilities: {
    currentLiabilities: FinancialStatementItem[];
    totalCurrentLiabilities: number;
    longTermLiabilities: FinancialStatementItem[];
    totalLongTermLiabilities: number;
    totalLiabilities: number;
  };
  equity: {
    items: FinancialStatementItem[];
    totalEquity: number;
  };
}

// 손익계산서
export interface IncomeStatement {
  periodStart: string;
  periodEnd: string;
  revenue: {
    items: FinancialStatementItem[];
    totalRevenue: number;
  };
  costOfGoodsSold: {
    items: FinancialStatementItem[];
    totalCOGS: number;
  };
  grossProfit: number;
  operatingExpenses: {
    items: FinancialStatementItem[];
    totalOperatingExpenses: number;
  };
  operatingIncome: number;
  otherIncome: {
    items: FinancialStatementItem[];
    totalOtherIncome: number;
  };
  otherExpenses: {
    items: FinancialStatementItem[];
    totalOtherExpenses: number;
  };
  netIncome: number;
}

// 상태 레이블 및 색상
export const journalEntryStatusConfig: Record<
  JournalEntryStatus,
  { label: string; color: string }
> = {
  draft: { label: '임시저장', color: 'gray' },
  submitted: { label: '확정', color: 'green' },
  cancelled: { label: '취소', color: 'red' },
};

export const paymentStatusConfig: Record<
  PaymentStatus,
  { label: string; color: string }
> = {
  draft: { label: '임시저장', color: 'gray' },
  submitted: { label: '확정', color: 'green' },
  cancelled: { label: '취소', color: 'red' },
};

export const paymentTypeConfig: Record<
  PaymentType,
  { label: string; color: string }
> = {
  receive: { label: '수금', color: 'blue' },
  pay: { label: '지급', color: 'orange' },
  internal_transfer: { label: '내부이체', color: 'purple' },
};

export const paymentMethodConfig: Record<
  PaymentMethod,
  { label: string }
> = {
  cash: { label: '현금' },
  bank_transfer: { label: '계좌이체' },
  check: { label: '수표' },
  card: { label: '카드' },
};

export const accountTypeConfig: Record<
  AccountType,
  { label: string; color: string }
> = {
  asset: { label: '자산', color: 'blue' },
  liability: { label: '부채', color: 'red' },
  equity: { label: '자본', color: 'purple' },
  income: { label: '수익', color: 'green' },
  expense: { label: '비용', color: 'orange' },
};
